import { load as yamlLoad, dump as yamlDump } from 'js-yaml';
import { findScreenById } from '../../lib/applyPatch';

export type ApplyTextEditResult = {
  screenYaml: string;
  found: boolean;
};

/**
 * Recursively walks a JS object and replaces all occurrences of `oldText`
 * with `newText` in any string value. Keys are never touched.
 */
function replaceInObject(
  node: unknown,
  oldText: string,
  newText: string,
  foundRef: { value: boolean }
): unknown {
  if (typeof node === 'string') {
    if (node.includes(oldText)) {
      foundRef.value = true;
      return node.split(oldText).join(newText);
    }
    return node;
  }
  if (Array.isArray(node)) {
    return node.map(item => replaceInObject(item, oldText, newText, foundRef));
  }
  if (node !== null && typeof node === 'object') {
    return Object.fromEntries(
      Object.entries(node as Record<string, unknown>).map(([k, v]) => [
        k,
        replaceInObject(v, oldText, newText, foundRef)
      ])
    );
  }
  return node;
}

/**
 * Applies a `<text-edit>` replacement directly to an already-serialized screen.
 *
 * Used to chain multiple text-edits on the same screen within one message so
 * later edits compose on top of earlier ones rather than each re-deriving from
 * the original course yaml.
 */
export function applyTextEditToScreenYaml(
  screenYaml: string,
  oldText: string,
  newText: string
): ApplyTextEditResult {
  let screen: unknown;
  try {
    screen = yamlLoad(screenYaml);
  } catch {
    return { screenYaml: '', found: false };
  }
  if (screen === null || typeof screen !== 'object') {
    return { screenYaml: '', found: false };
  }

  const foundRef = { value: false };
  const updated = replaceInObject(screen, oldText, newText, foundRef);
  if (!foundRef.value) {
    return { screenYaml: '', found: false };
  }

  return { screenYaml: yamlDump(updated, { lineWidth: -1 }), found: true };
}

/**
 * Applies a `<text-edit>` replacement to a single screen within the course.
 *
 * Finds the screen by `screenId`, replaces `oldText` with `newText` in all
 * string values, and returns the updated screen as a YAML string.
 *
 * Returns `found: false` when the screen doesn't exist or `oldText` isn't
 * present in any string value of the screen object.
 */
export default function applyTextEdit(
  currentYaml: string,
  screenId: string,
  oldText: string,
  newText: string
): ApplyTextEditResult {
  let courseObj: Content;
  try {
    courseObj = yamlLoad(currentYaml) as Content;
  } catch {
    return { screenYaml: '', found: false };
  }

  const screen = findScreenById(courseObj, screenId);
  if (!screen) {
    return { screenYaml: '', found: false };
  }

  const foundRef = { value: false };
  const updated = replaceInObject(screen, oldText, newText, foundRef);

  if (!foundRef.value) {
    return { screenYaml: '', found: false };
  }

  const screenYaml = yamlDump(updated, { lineWidth: -1 });
  return { screenYaml, found: true };
}
